import React, { useState } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Image from './AppImage';
import { submitReview } from '../services/reviewService';
import toast from 'react-hot-toast';

const LABELS = ['Terrible', 'Poor', 'Average', 'Good', 'Excellent'];

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="group focus:outline-none"
        aria-label={`${n} star${n > 1 ? 's' : ''}`}
      >
        <svg
          viewBox="0 0 20 20"
          className={`w-8 h-8 transition-colors ${
            n <= value
              ? 'text-amber-400 group-hover:text-amber-500'
              : 'text-border group-hover:text-amber-300'
          }`}
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.97c.3.922-.755 1.688-1.54 1.118l-3.38-2.455a1 1 0 00-1.175 0l-3.38 2.455c-.784.57-1.838-.196-1.54-1.118l1.287-3.97a1 1 0 00-.363-1.118L2.05 9.397c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.97z" />
        </svg>
      </button>
    ))}
    {value > 0 && (
      <span className="text-sm font-semibold text-amber-500 ml-1">
        {LABELS[value - 1]}
      </span>
    )}
  </div>
);

/**
 * ReviewModal
 *
 * Props:
 *   expert       { id (expert_profile UUID), full_name, title, avatar_url }
 *   contractId   UUID — the completed contract being reviewed
 *   isOpen       boolean
 *   onClose      () => void
 *   onSubmitted  () => void  — called after successful submission
 */
const ReviewModal = ({ expert, contractId, isOpen, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !expert || !contractId) return null;

  const handleClose = () => {
    setRating(0);
    setTitle('');
    setComment('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    if (!comment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitReview({
        contractId,
        expertId: expert.id,
        rating,
        title,
        comment,
      });
      if (result.success) {
        toast.success('Review submitted — thank you!');
        onSubmitted?.();
        handleClose();
      } else {
        toast.error(result.error || 'Failed to submit review');
      }
    } catch {
      toast.error('Unexpected error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Sheet */}
      <div className="relative z-10 bg-card w-full sm:max-w-md sm:mx-4 max-h-[95vh] sm:max-h-[85vh] rounded-t-3xl sm:rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden">
        {/* Mobile handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-foreground">Leave a Review</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Share your experience with this expert</p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Expert strip */}
        <div className="flex items-center gap-3 px-5 py-3 bg-primary/5 border-b border-border flex-shrink-0">
          {expert.avatar_url ? (
            <Image
              src={expert.avatar_url}
              alt={expert.full_name}
              className="w-10 h-10 rounded-xl object-cover ring-2 ring-border"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={18} className="text-primary" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground truncate">{expert.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">{expert.title}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Stars */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Overall Rating <span className="text-destructive">*</span>
            </label>
            <StarPicker value={rating} onChange={setRating} />
          </div>

          {/* Headline */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Headline <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Outstanding analysis, very thorough"
              className={inputClass}
              maxLength={120}
            />
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Your Review <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Describe your experience — quality of work, communication, professionalism…"
              className={`${inputClass} resize-none`}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {comment.length}/2000
            </p>
          </div>

          {/* Disclaimer */}
          <div className="flex gap-3 p-3.5 bg-muted/50 border border-border rounded-xl">
            <Icon name="Info" size={15} className="text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Reviews are public and visible to all users of the marketplace. You can only leave one review per contract.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-border bg-muted/20 flex-shrink-0">
          <Button variant="outline" className="sm:w-28" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="default"
            className="flex-1"
            disabled={submitting || rating === 0}
            iconName={submitting ? 'Loader' : 'Star'}
            iconPosition="left"
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit Review'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
